import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AdminLayout } from '../../layouts/AdminLayout';
import { useCreateCase } from '../../hooks/useCreateCase';
import { SimpleCaseForm } from '../../components/organisms/CaseForm';
import type { SimpleCaseFormData } from '../../components/organisms/CaseForm';
import { toast } from '../../utils/toast';
import type { CreateCaseDto } from '@lnr-v2/types';
import { Button } from '../../components/atoms/Button';

export const CaseCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientIdParam = searchParams.get('clientId');

  const { createCase, loading: creating } = useCreateCase();

  const handleSubmit = async (data: SimpleCaseFormData) => {
    try {
      // Helper function to convert empty strings to undefined
      const sanitizeOptionalField = (
        value: string | undefined
      ): string | undefined => {
        return value && value.trim() !== '' ? value : undefined;
      };

      // Convert simplified form data to CreateCaseDto with defaults
      const createDto: CreateCaseDto = {
        title: data.title,
        clientId: data.clientId,
        slogan: sanitizeOptionalField(data.slogan),
        shortDescription: sanitizeOptionalField(data.shortDescription),
        featuredImage: data.featuredImage, // Now it's already a URL from ImageUploader
        thumbnailImage: data.mobileImage,
        awards: data.awards,
        termIds: data.termIds,
        isFeatured: data.isFeatured,
        sortOrder: data.sortOrder,
        status: data.status, // Use form status (defaults to DRAFT)
      };

      const createdCase = await createCase(createDto);

      // Show success toast with translation action
      toast.success('Case created successfully!', {
        duration: 5000,
        action: {
          label: 'Add Translations',
          onClick: () =>
            navigate(`/admin/translations?filter=cases.${createdCase.slug}`),
        },
      });

      // Navigate to edit page so user can see preview URL and add layout
      setTimeout(() => navigate(`/admin/cases/${createdCase.id}/edit`), 1500);
    } catch (err) {
      // Show error toast for immediate feedback
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create case';
      toast.error(errorMessage);
    }
  };

  const handleCancel = () => {
    navigate('/admin/cases');
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8" aria-label="Breadcrumb">
          <Button
            variant="link"
            size="sm"
            onClick={() => navigate('/admin/cases')}
          >
            Back to all cases
          </Button>
        </nav>

        {/* Form - Renders immediately, ClientSelector handles async data */}
        <SimpleCaseForm
          initialClientId={clientIdParam || undefined}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={creating}
        />
      </div>
    </AdminLayout>
  );
};
